import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LogLayout from "../components/LogLayout";
import SectionTabs, { TOURS_TABS } from "../components/SectionTabs";
import { useAuth } from "../App";
import { Package, Plus, X, MapPin, User, Phone, AlertTriangle, Clock, CheckCircle2, Truck, Eye, Image as ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const COURSE_STATUSES = [
  { value: "created", label: "Créée", color: "bg-white/50/20 text-slate-400 border-slate-500/30" },
  { value: "assigned", label: "Assignée", color: "bg-blue-500/100/20 text-blue-400 border-blue-500/30" },
  { value: "picked_up", label: "Récupérée", color: "bg-cyan-500/100/20 text-cyan-400 border-cyan-500/30" },
  { value: "in_transit", label: "En transit", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "delivered", label: "Livrée", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  { value: "completed", label: "Terminée", color: "bg-green-500/100/20 text-green-400 border-green-500/30" },
  { value: "failed", label: "Échouée", color: "bg-red-500/100/20 text-red-400 border-red-500/30" },
];

const PRIORITIES = [
  { value: "normal", label: "Normal", color: "text-slate-400" },
  { value: "urgent", label: "Urgent", color: "text-amber-400" },
  { value: "critique", label: "Critique", color: "text-red-400" },
];

const getStatusStyle = (s) => COURSE_STATUSES.find(st => st.value === s)?.color || "";
const getStatusLabel = (s) => COURSE_STATUSES.find(st => st.value === s)?.label || s;
const getPriorityColor = (p) => PRIORITIES.find(pr => pr.value === p)?.color || "text-slate-400";

export default function LogCoursesPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [stats, setStats] = useState(null);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [form, setForm] = useState({
    course_type: "generale", title: "", description: "", pickup_address: "", pickup_contact: "", pickup_phone: "",
    delivery_address: "", delivery_contact: "", delivery_phone: "", courier_id: "", courier_name: "", priority: "normal", note: ""
  });

  const isCourier = user?.role === "coursier" || user?.role === "agent commercial";

  const load = useCallback(async () => {
    try {
      const h = getHeaders();
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.course_type = filterType;

      const [coursesRes, statsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/log/courses`, { headers: h, params }),
        axios.get(`${API}/log/courses/stats`, { headers: h }),
        axios.get(`${API}/log/courses/pending-reminders`, { headers: h })
      ]);
      setCourses(coursesRes.data.courses || []);
      setStats(statsRes.data);
      setPendingReminders(remindersRes.data.pending_courses || []);

      // Load couriers if not a courier
      if (!isCourier) {
        try {
          const courRes = await axios.get(`${API}/log/courses/couriers/list`, { headers: h });
          setCouriers(courRes.data.couriers || []);
        } catch (e) { /* ignore if no couriers */ }
      }
    } catch { }
    finally { setLoading(false); }
  }, [filterStatus, filterType, isCourier]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (body.courier_id) {
        const c = couriers.find(c => c.id === body.courier_id);
        body.courier_name = c?.full_name || "";
      }
      await axios.post(`${API}/log/courses`, body, { headers: getHeaders() });
      toast.success("Course creee");
      setShowCreate(false);
      setForm({ course_type: "generale", title: "", description: "", pickup_address: "", pickup_contact: "", pickup_phone: "", delivery_address: "", delivery_contact: "", delivery_phone: "", courier_id: "", courier_name: "", priority: "normal", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleStatusUpdate = async (courseId, newStatus, note) => {
    try {
      await axios.put(`${API}/log/courses/${courseId}/status`, { status: newStatus, note: note || "" }, { headers: getHeaders() });
      toast.success("Statut mis a jour");
      setShowStatusUpdate(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/log/courses/${id}`, { headers: getHeaders() });
      setShowDetail(res.data.course);
    } catch (e) { toast.error("Erreur chargement"); }
  };

  return (
    <LogLayout title="Tournées" subtitle={isCourier ? "Vos courses assignées" : "Courses : gestion des courses et agents commerciaux"}>
      {!isCourier && <SectionTabs tabs={TOURS_TABS} />}
      <div data-testid="log-courses-page" className="space-y-4">
        {/* Reminder Banner */}
        {pendingReminders.length > 0 && (
          <div className="bg-amber-500/100/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3" data-testid="pending-reminders-banner">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-300">{pendingReminders.length} course(s) en attente</p>
              <p className="text-xs text-amber-400/70">Des courses necessitent votre attention</p>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && stats.total > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-slate-900 border border-white/5 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 uppercase">Total courses</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-slate-900 border border-white/5 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 uppercase">En attente</p>
              <p className="text-xl font-bold text-amber-400">{stats.pending_count}</p>
            </div>
            <div className="bg-slate-900 border border-white/5 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 uppercase">Taux succès</p>
              <p className="text-xl font-bold text-emerald-400">{stats.success_rate}%</p>
            </div>
            <div className="bg-slate-900 border border-white/5 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 uppercase">Par type</p>
              <div className="flex gap-2 mt-1">
                {Object.entries(stats.by_type || {}).map(([t, c]) => (
                  <span key={t} className="text-xs text-slate-300">{t === "assurance" ? "Ass." : "Gen."}: {c}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-2">
            <select data-testid="course-filter-status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLoading(true); }} className="bg-slate-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
              <option value="">Tous les statuts</option>
              {COURSE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select data-testid="course-filter-type" value={filterType} onChange={e => { setFilterType(e.target.value); setLoading(true); }} className="bg-sle-900 border border-white/10 rounded-lg text-sm text-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none">
              <option value="">Tous les types</option>
              <option value="assurance">Assurance</option>
              <option value="generale">Générale</option>
            </select>
          </div>
          {!isCourier && (
            <Button data-testid="create-course-btn" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2">
              <Plus className="w-4 h-4" /> Nouvelle course
            </Button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16 bg-slate-900 rounded-xl border border-white/5">
            <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">{isCourier ? "Aucune course assignee" : "Aucune course creee"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {courses.map(course => (
              <div key={course.id} data-testid={`course-row-${course.id}`} className="bg-slate-900 border border-white/5 rounded-xl p-4 hover:border-blue-500/20 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${course.course_type === "assurance" ? "bg-blue-500/100/15" : "bg-violet-500/15"}`}>
                      {course.course_type === "assurance" ? <Truck className="w-5 h-5 text-blue-400" /> : <Package className="w-5 h-5 text-violet-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-white">{course.title}</p>
                        <span className={`text-[10px] font-medium ${getPriorityColor(course.priority)}`}>[{course.priority}]</span>
                        <span className="text-[10px] text-slate-600 bg-slate-800 px-1.5 py-0.5 rounded">{course.course_type === "assurance" ? "Assurance" : "Générale"}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{course.pickup_address}</span>
                        <span>→</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{course.delivery_address}</span>
                      </div>
                      {course.courier_name && <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><User className="w-3 h-3" />{course.courier_name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => loadDetail(course.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-blue-400" data-testid={`view-course-${course.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => setShowStatusUpdate(course)} className={`px-2.5 py-1 rounded-full border text-[11px] font-medium cursor-pointer hover:opacity-80 ${getStatusStyle(course.status)}`} data-testid={`course-status-${course.id}`}>
                      {getStatusLabel(course.status)}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="create-course-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">Nouvelle course</h2>
                <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Type *</label>
                    <select data-testid="course-type-select" value={form.course_type} onChange={e => setForm(f => ({ ...f, course_type: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                      <option value="generale">Générale</option>
                      <option value="assurance">Assurance</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Priorite</label>
                    <select data-testid="course-priority-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                      {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Titre *</label>
                  <input data-testid="course-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" placeholder="Ex: Dépôt factures NSIA" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Description</label>
                  <textarea data-testid="course-desc-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none resize-none" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Lieu recup. *</label>
                    <input data-testid="course-pickup-addr" value={form.pickup_address} onChange={e => setForm(f => ({ ...f, pickup_address: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Contact recup.</label>
                    <input data-testid="course-pickup-contact" value={form.pickup_contact} onChange={e => setForm(f => ({ ...f, pickup_contact: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Tel recup.</label>
                    <input data-testid="course-pickup-phone" value={form.pickup_phone} onChange={e => setForm(f => ({ ...f, pickup_phone: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Lieu livr. *</label>
                    <input data-testid="course-delivery-addr" value={form.delivery_address} onChange={e => setForm(f => ({ ...f, delivery_address: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Contact livr.</label>
                    <input data-testid="course-delivery-contact" value={form.delivery_contact} onChange={e => setForm(f => ({ ...f, delivery_contact: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Tel livr.</label>
                    <input data-testid="course-delivery-phone" value={form.delivery_phone} onChange={e => setForm(f => ({ ...f, delivery_phone: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Agent Commercial</label>
                  <select data-testid="course-courier-select" value={form.courier_id} onChange={e => setForm(f => ({ ...f, courier_id: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                    <option value="">Non assigne</option>
                    {couriers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
                  <Button type="submit" data-testid="submit-course-btn" className="flex-1 bg-blue-600 hover:bg-blue-500/100 text-white">Creer</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {showStatusUpdate && (
          <CourseStatusModal course={showStatusUpdate} isCourier={isCourier} onClose={() => setShowStatusUpdate(null)} onUpdate={handleStatusUpdate} />
        )}

        {/* Détail Modal */}
        {showDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="course-detail-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">{showDetail.title}</h2>
                <button onClick={() => setShowDetail(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-[10px] text-slate-500 uppercase">Type</p><p className="text-sm text-white">{showDetail.course_type === "assurance" ? "Assurance" : "Générale"}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Priorite</p><p className={`text-sm font-medium ${getPriorityColor(showDetail.priority)}`}>{showDetail.priority}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Recuperation</p><p className="text-sm text-white">{showDetail.pickup_address}</p><p className="text-[10px] text-slate-500">{showDetail.pickup_contact} {showDetail.pickup_phone}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Livraison</p><p className="text-sm text-white">{showDetail.delivery_address}</p><p className="text-[10px] text-slate-500">{showDetail.delivery_contact} {showDetail.delivery_phone}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Agent Commercial</p><p className="text-sm text-white">{showDetail.courier_name || "Non assigne"}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Créé par</p><p className="text-sm text-white">{showDetail.created_by_name}</p></div>
                </div>
                {showDetail.description && <div><p className="text-[10px] text-slate-500 uppercase">Description</p><p className="text-sm text-slate-300">{showDetail.description}</p></div>}

                {/* Proofs from tracking */}
                {showDetail.tracking?.some(t => t.proof_photo || t.proof_signature) && (
                  <div data-testid="admin-proofs-section">
                    <p className="text-xs text-sky-400 font-semibold mb-2 uppercase flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> Preuves collectees par le agent commercial
                    </p>
                    <div className="space-y-3">
                      {showDetail.tracking.filter(t => t.proof_photo || t.proof_signature).map((t, i) => (
                        <div key={i} className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-3">
                          <p className="text-[10px] text-sky-300 font-medium mb-2">
                            {getStatusLabel(t.status)} — {new Date(t.date).toLocaleString("fr-FR")}
                          </p>
                          <div className="flex gap-3">
                            {t.proof_photo && (
                              <div data-testid={`admin-proof-photo-${t.status}`}>
                                <p className="text-[9px] text-slate-500 mb-1">Photo</p>
                                <img src={t.proof_photo} alt="Preuve photo" className="w-28 h-20 object-cover rounded-lg border border-white/10 cursor-pointer hover:opacity-80"
                                  onClick={() => window.open(t.proof_photo, '_blank')} />
                              </div>
                            )}
                            {t.proof_signature && (
                              <div data-testid={`admin-proof-signature-${t.status}`}>
                                <p className="text-[9px] text-slate-500 mb-1">Signature</p>
                                <img src={t.proof_signature} alt="Signature" className="w-28 h-20 object-contain rounded-lg border border-white/10 bg-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showDetail.tracking?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 font-semibold mb-2 uppercase">Suivi</p>
                    <div className="space-y-2">
                      {showDetail.tracking.map((t, i) => (
                        <div key={i} className="flex items-start gap-3 p-2 bg-slate-950 rounded-lg">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${getStatusStyle(t.status).split(" ")[0]}`} />
                          <div>
                            <p className="text-xs text-white">{getStatusLabel(t.status)} <span className="text-slate-500 ml-1">{t.note}</span></p>
                            <p className="text-[10px] text-slate-500">{new Date(t.date).toLocaleString("fr-FR")} - {t.by}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}

function CourseStatusModal({ course, isCourier, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const allowedStatuses = isCourier
    ? COURSE_STATUSES.filter(s => ["picked_up", "in_transit", "delivered", "completed", "failed"].includes(s.value))
    : COURSE_STATUSES;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="course-status-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-base font-bold text-white">Modifier le statut</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-slate-400 mb-3">Course: <span className="text-white">{course.title}</span></p>
          {allowedStatuses.map(s => (
            <button key={s.value} onClick={() => onUpdate(course.id, s.value, note)} data-testid={`set-course-status-${s.value}`}
              className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${course.status === s.value ? s.color + " font-semibold" : "border-white/5 text-slate-400 hover:bg-white/5"}`}>
              {s.label} {course.status === s.value && "(actuel)"}
            </button>
          ))}
          <div className="pt-3 border-t border-white/5 mt-3">
            <label className="text-xs text-slate-400 block mb-1">Note</label>
            <input data-testid="course-status-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Commentaire optionnel" className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
